import {constrainMintedClientId, peekAnalyticsClientId} from "analytics/analyticsClientId";
import {sendAnalyticsEvent} from "analytics/analyticsEventClient";
import type {AnalyticsEventName} from "analytics/analyticsEvents";
import {browserSessionStorage} from "core/browserStorage";
import {getConsent} from "consent/consentStore";
import {armForClientId, createPageLoadArm, resolveGameArm} from "./gameArm";
import type {GameEventParams} from "./gameSession";

/**
 * The browser half of the transfer-game rules (kitecorp/transferify-ui#2872): where the arm's inputs,
 * the best score and the event transport come from. React-free and lazy: nothing here reads browser
 * state until it is called, so the pre-rendered landing that imports it touches nothing.
 */

/**
 * This page load's A/B group. The client id counts only while analytics is granted: without consent
 * there is no id, and the visitor gets a fair draw that is stored nowhere.
 *
 * A drawn group is also what any id minted later in this page load must derive to
 * (kitecorp/transferify-ui#2888): the landing asks for the group on arrival, usually before the
 * visitor answers the cookie banner, and a visitor who then grants analytics must not start reporting
 * under the other group on their next page load.
 */
export const transferGamesArm = createPageLoadArm(() => {
	const clientId = getConsent().analytics ? peekAnalyticsClientId() : null;
	const arm = resolveGameArm(clientId, Math.random);
	if (clientId === null) constrainMintedClientId(id => armForClientId(id) === arm);
	return arm;
});

/** Sends one game event through the consent-gated first-party path; `null` means "nothing to send". */
export function reportGameEvent(name: AnalyticsEventName, params: GameEventParams | null): void {
	if (params !== null) {
		sendAnalyticsEvent(name, params);
	}
}

/**
 * The storage area the best score lives in, or null outside a browser. The TAB's session storage, never
 * local storage (owner decision 2026-09-24 on kitecorp/transferify-ui#2880): the Cookie Policy lists the
 * keys as needing no consent, and an independent legal check accepted that for what is written, when
 * and for whom, but not for a copy that outlives the visit. "Until you close the browser tab" is what
 * the policy now promises in three languages, and `gameAdapters.test.ts` pins this seam to it.
 */
export function bestScoreStorage(): Storage | null {
	return browserSessionStorage();
}
